/*
 * SystemAboutCard - System 信息卡片
 * 合并 About 信息和环境配置信息，展示 System 的完整信息
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Typography,
  Tooltip,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LinkIcon from '@material-ui/icons/Link';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import { KubernetesIcon } from './KubernetesIcon';
import IconButton from '@material-ui/core/IconButton';
import { useRouteRef, useApi } from '@backstage/core-plugin-api';
import { useEntity, useAsyncEntity, entityRouteRef, catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import {
  parseEnvironmentConfigs,
  getArgoCDProjectInfo,
  getKnativeResourceRefs,
  isKnativeManaged,
  environmentColors,
} from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    contentGrid: {
      padding: theme.spacing(2, 0, 0, 0), // 16px 0 0 0
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    leftColumn: {
      flex: '0 0 28%', // 28% 宽度
      paddingRight: theme.spacing(2),
      borderRight: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('sm')]: {
        flex: '0 0 100%',
        borderRight: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingRight: 0,
        paddingBottom: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
    },
    rightColumn: {
      flex: '0 0 72%', // 72% 宽度
      paddingLeft: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        flex: '0 0 100%',
        paddingLeft: 0,
        paddingTop: theme.spacing(2),
      },
    },
    section: {
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    sectionTitle: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    infoRow: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      minWidth: 60,
      marginRight: theme.spacing(1),
      flexShrink: 0,
    },
    infoValue: {
      fontSize: '0.875rem',
      flex: 1,
      wordBreak: 'break-word',
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
    },
    envChip: {
      fontSize: '0.7rem',
      height: 22,
    },
    envGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(1),
    },
    envItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1.25),
      borderRadius: theme.spacing(1),
      position: 'relative',
    },
    envHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    envInfo: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.25),
      '& svg': {
        width: '1rem',
        height: '1.3rem',
        flexShrink: 0,
      },
    },
    envDetail: {
      fontSize: '0.65rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      lineHeight: 1.4,
    },
    argocdSource: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: theme.spacing(1),
    },
    knativeSource: {
      padding: theme.spacing(2),
      backgroundColor: '#1976d2',  // Knative 蓝色
      color: '#fff',
      borderRadius: theme.spacing(1),
    },
    argocdSourceTitle: {
      fontSize: '0.75rem',
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    argocdSourceInfo: {
      fontSize: '0.8rem',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    argocdSourceRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    argocdSourceLabel: {
      fontSize: '0.7rem',
      opacity: 0.9,
      minWidth: 60,
    },
    argocdSourceValue: {
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    argocdInfo: {
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.spacing(0.75),
      marginTop: theme.spacing(1),
    },
    argocdRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.75),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    argocdLabel: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      minWidth: 80,
      flexShrink: 0,
    },
    argocdValue: {
      fontSize: '0.8rem',
      fontFamily: 'monospace',
      wordBreak: 'break-all',
      flex: 1,
    },
    ownerLink: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    divider: {
      margin: theme.spacing(1.5, 0),
    },
  }),
);

export const SystemAboutCard: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { refresh } = useAsyncEntity();
  const entityRoute = useRouteRef(entityRouteRef);
  const catalogApi = useApi(catalogApiRef);

  const isKnative = isKnativeManaged(entity);
  const environments = parseEnvironmentConfigs(entity);
  const argocdProject = getArgoCDProjectInfo(entity);
  
  // 使用 useMemo 缓存 knativeResources，避免无限循环
  const knativeResources = useMemo(() => getKnativeResourceRefs(entity), [entity]);

  // 使用 useMemo 缓存 resourceRef，避免无限循环
  const argocdProjectResourceRef = useMemo(() => argocdProject?.resourceRef, [argocdProject?.resourceRef]);

  // 异步获取 ArgoCD Project Resource entity 的详细信息
  const [argocdProjectResource, setArgoCDProjectResource] = useState<Entity | undefined>(undefined);

  // 异步获取 Knative Resource entities 的详细信息
  const [knativeResourceEntities, setKnativeResourceEntities] = useState<Record<string, Entity>>({});

  useEffect(() => {
    let cancelled = false;
    const fetchResource = async () => {
      if (argocdProjectResourceRef) {
        try {
          const resource = await catalogApi.getEntityByRef(argocdProjectResourceRef);
          if (!cancelled) {
            setArgoCDProjectResource(resource);
          }
        } catch (e) {
          // console.error(`Failed to fetch ArgoCD Project Resource ${argocdProjectResourceRef}:`, e);
          if (!cancelled) {
            setArgoCDProjectResource(undefined);
          }
        }
      } else {
        if (!cancelled) {
          setArgoCDProjectResource(undefined);
        }
      }
    };
    fetchResource();
    return () => {
      cancelled = true;
    };
  }, [argocdProjectResourceRef, catalogApi]);

  // 异步获取 Knative Resource entities
  useEffect(() => {
    if (!isKnative || knativeResources.length === 0) {
      setKnativeResourceEntities({});
      return;
    }

    let cancelled = false;
    const fetchKnativeResources = async () => {
      const entities: Record<string, Entity> = {};
      for (const res of knativeResources) {
        try {
          const resource = await catalogApi.getEntityByRef(res.resourceRef);
          if (resource && !cancelled) {
            entities[res.environment] = resource;
          }
        } catch (e) {
          // ignore fetch errors
        }
      }
      if (!cancelled) {
        setKnativeResourceEntities(entities);
      }
    };
    fetchKnativeResources();
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [isKnative, knativeResources, catalogApi]);

  // 解析 owner 路径
  let ownerPath: string | undefined;
  const owner = entity.spec?.owner as string | undefined;
  if (owner) {
    const ownerParts = owner.split('/');
    const ownerName = ownerParts[ownerParts.length - 1];
    const ownerKind = owner.split(':')[0] || 'group';
    const ownerNamespace = ownerParts.length > 1 
      ? ownerParts[0].split(':')[1] || 'default'
      : 'default';
    ownerPath = entityRoute({
      kind: ownerKind,
      namespace: ownerNamespace,
      name: ownerName,
    });
  }

  // 解析 ArgoCD destinations 并与环境匹配
  const argocdDestinations = useMemo(() => {
    if (!argocdProjectResource?.metadata.annotations?.['hose.io/destinations']) {
      return [];
    }
    try {
      const destinations = JSON.parse(
        argocdProjectResource.metadata.annotations['hose.io/destinations']
      );
      if (Array.isArray(destinations)) {
        return destinations;
      }
    } catch (e) {
      // console.error("Failed to parse destinations JSON:", e);
    }
    return [];
  }, [argocdProjectResource]);

  // 匹配环境与 ArgoCD destination（Knative 模式下直接标记为已匹配）
  const matchedEnvironments = useMemo(() => {
    return environments.map(env => {
      // Knative 模式下，环境配置来自 annotations，直接视为已匹配
      if (isKnative) {
        return {
          ...env,
          matchedDestination: null,
          isMatched: true,  // Knative 环境直接标记为已匹配
        };
      }
      // ArgoCD 模式：根据 clusterName 或 server 匹配 destination
      const matchedDest = argocdDestinations.find((dest: any) => {
        return (
          dest.clusterName === env.clusterName ||
          dest.name === env.clusterName ||
          dest.server === env.server
        );
      });
      return {
        ...env,
        matchedDestination: matchedDest,
        isMatched: !!matchedDest,
      };
    });
  }, [environments, argocdDestinations, isKnative]);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.contentGrid}>
          {/* 左侧：System 基本信息 */}
          <Box className={classes.leftColumn}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <Typography variant="h5" style={{ fontWeight: 600 }}>
                About
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {refresh && (
                  <IconButton
                    size="small"
                    onClick={refresh}
                    aria-label="Refresh"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                )}
                {entity.metadata.annotations?.['backstage.io/edit-url'] && (
                  <IconButton
                    size="small"
                    href={entity.metadata.annotations['backstage.io/edit-url']}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Edit"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>基本信息</Typography>
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>名称</Typography>
                <Typography className={classes.infoValue} style={{ fontWeight: 600 }}>
                  {entity.metadata.name}
                </Typography>
              </Box>
              
              {entity.metadata.description && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>描述</Typography>
                  <Typography className={classes.infoValue}>{entity.metadata.description}</Typography>
                </Box>
              )}

              {owner && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>Owner</Typography>
                  <Typography className={classes.infoValue}>
                    {ownerPath ? (
                      <Link href={ownerPath} className={classes.ownerLink}>
                        {owner}
                      </Link>
                    ) : (
                      owner
                    )}
                  </Typography>
                </Box>
              )}

              {entity.metadata.tags && entity.metadata.tags.length > 0 && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>标签</Typography>
                  <Box className={classes.tagsContainer}>
                    {entity.metadata.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" className={classes.envChip} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* 右侧：数据源和环境配置 */}
          <Box className={classes.rightColumn}>
            {/* Knative 数据源 */}
            {isKnative && (
              <Box className={classes.section}>
                <Box className={classes.knativeSource}>
                  <Typography className={classes.argocdSourceTitle}>
                    <LinkIcon fontSize="small" />
                    数据源：Knative Serverless
                  </Typography>
                  <Box className={classes.argocdSourceInfo}>
                    <Box className={classes.argocdSourceRow}>
                      <Typography className={classes.argocdSourceLabel}>管理方式</Typography>
                      <Typography className={classes.argocdSourceValue}>
                        Knative Serving
                      </Typography>
                    </Box>
                    {/* 显示集群列表（去重） */}
                    {(() => {
                      const clusters = new Map<string, string>();
                      Object.values(knativeResourceEntities).forEach(res => {
                        const clusterName = res?.metadata.annotations?.['hose.io/knative-cluster'];
                        const clusterUrl = res?.metadata.annotations?.['hose.io/knative-cluster-url'];
                        if (clusterName && !clusters.has(clusterName)) {
                          clusters.set(clusterName, clusterUrl || '');
                        }
                      });
                      const clusterList = Array.from(clusters.entries());
                      if (clusterList.length === 0) return null;
                      return (
                        <Box className={classes.argocdSourceRow}>
                          <Typography className={classes.argocdSourceLabel}>
                            {clusterList.length > 1 ? '集群' : '集群'}
                          </Typography>
                          <Typography className={classes.argocdSourceValue}>
                            {clusterList.length} 个目标集群
                          </Typography>
                        </Box>
                      );
                    })()}
                    <Box className={classes.argocdSourceRow}>
                      <Typography className={classes.argocdSourceLabel}>Resources</Typography>
                      <Typography className={classes.argocdSourceValue}>
                        {knativeResources.length} 个环境资源
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ArgoCD 数据源（非 Knative 时显示） */}
            {!isKnative && argocdProject && (
              <Box className={classes.section}>
                {argocdProjectResource ? (
                  <Box className={classes.argocdSource}>
                    <Typography className={classes.argocdSourceTitle}>
                      <LinkIcon fontSize="small" />
                      数据源：ArgoCD Project
                    </Typography>
                    <Box className={classes.argocdSourceInfo}>
                      <Box className={classes.argocdSourceRow}>
                        <Typography className={classes.argocdSourceLabel}>实例</Typography>
                        <Typography className={classes.argocdSourceValue}>
                          {argocdProject.instanceName}
                        </Typography>
                      </Box>
                      <Box className={classes.argocdSourceRow}>
                        <Typography className={classes.argocdSourceLabel}>项目</Typography>
                        <Typography className={classes.argocdSourceValue}>
                          {argocdProject.projectName}
                        </Typography>
                      </Box>
                      {argocdProjectResource.metadata.annotations?.['argocd.argoproj.io/instance-url'] && (
                        <Box className={classes.argocdSourceRow}>
                          <Typography className={classes.argocdSourceLabel}>地址</Typography>
                          <Tooltip 
                            title={argocdProjectResource.metadata.annotations['argocd.argoproj.io/instance-url']} 
                            arrow
                          >
                            <Typography 
                              className={classes.argocdSourceValue} 
                              style={{ cursor: 'help', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            >
                              {argocdProjectResource.metadata.annotations['argocd.argoproj.io/instance-url']}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                      <Box className={classes.argocdSourceRow}>
                        <Typography className={classes.argocdSourceLabel}>Destinations</Typography>
                        <Typography className={classes.argocdSourceValue}>
                          {argocdDestinations.length} 个目标集群
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.argocdInfo} style={{ border: '1px dashed', borderColor: '#ff9800' }}>
                    <Typography variant="body2" style={{ color: '#ff9800' }}>
                      ArgoCD Project 资源加载中...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* 环境配置（关联 ArgoCD destinations 或 Knative destinations） */}
            <Box className={classes.section}>
              {matchedEnvironments.length === 0 ? (
                <EmptyState
                  missing="info"
                  title="未配置环境"
                  description={
                    isKnative
                      ? '请在 annotations 中添加 hose.io/knative-dest-* 配置'
                      : '请在 annotations 中添加 hose.io/argocd-dest-* 配置'
                  }
                />
              ) : (
                <Box className={classes.envGrid}>
                  {matchedEnvironments.map((env) => {
                    const colors = environmentColors[env.name] || environmentColors.test;
                    return (
                      <Box
                        key={env.name}
                        className={classes.envItem}
                        style={{
                          backgroundColor: colors.bg,
                          border: `2px solid ${env.isMatched ? colors.border : '#ccc'}`,
                          opacity: env.isMatched ? 1 : 0.7,
                        }}
                      >
                        <Box className={classes.envHeader}>
                          <Chip
                            label={env.label}
                            size="small"
                            className={classes.envChip}
                            style={{
                              backgroundColor: colors.border,
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                          {env.isMatched && (
                            <Tooltip title="已匹配到 ArgoCD Destination" arrow>
                              <CheckCircleIcon 
                                fontSize="small" 
                                style={{ color: '#4caf50', fontSize: '1rem' }} 
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box className={classes.envInfo}>
                          <KubernetesIcon 
                            style={{ 
                              color: colors.text,
                              marginTop: '-3px',
                            }} 
                          />
                          <Typography variant="caption" style={{ color: colors.text, fontWeight: 500 }}>
                            {env.clusterName}
                          </Typography>
                        </Box>
                        {/* Destination 详细信息 */}
                        <Box className={classes.envDetail}>
                          <Typography variant="caption" style={{ fontSize: '0.65rem', opacity: 0.8, marginBottom: 4, display: 'block' }}>
                            Destination:
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: '0.65rem', display: 'block', fontFamily: 'monospace' }}>
                            k8s: {env.server}
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: '0.65rem', display: 'block', fontFamily: 'monospace' }}>
                            Namespace: {env.namespace}
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: '0.65rem', display: 'block', fontFamily: 'monospace' }}>
                            Name: {env.clusterName}
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: '0.65rem', display: 'block', fontFamily: 'monospace' }}>
                            ClusterName: {env.clusterName}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

